import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectRewards, setSurveyIsCompleted } from '@/store/reducers/survey/surveySlice';
import { RewardStatus, RewardType } from '@/constants/rewards';
import { onCollet } from '@/store/reducers/survey/actions';
import { ROUTES } from '@/constants/routes';
import useSurveyNavigate from '@/hooks/useSurveyNavigate';
import RewardsForm from '../forms/RewardsForm';
import analytics from '@/utils/analytics/analyticsInstance';

const RewardsStep: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { next } = useSurveyNavigate();
  const rewards = useAppSelector(selectRewards);
  const hasCompletedReward = rewards.some((item) => item.status === RewardStatus.COMPLETED);
  const title = hasCompletedReward ? 'Collect your reward' : 'Collect your first 50 points';
  const totalPoints = rewards.reduce((sum, reward) => {
    return reward.status === RewardStatus.COMPLETED ? sum + reward.points : sum;
  }, 0);

  const handleContinue = async (reward: RewardType) => {
    await dispatch(onCollet(reward.id));
    await analytics.track('webOnboardingClaimClicked', {
      task_name: reward.name,
      points_claimed: reward.points,
      points_claimed_total: totalPoints,
    });

    if (reward.id === 3) {
      dispatch(setSurveyIsCompleted(true));
      navigate(ROUTES.DOWNLOAD_APP);
      return;
    } else if (reward.id === 4) return;

    next();
  };

  useEffect(() => {
    analytics.track('webOnboardingScreenOpened', {
      screen_title: 'Rewards',
    });
  }, []);

  useEffect(() => {
    const allCompleted = rewards.every((item) => item.status === RewardStatus.COMPLETED);

    if (allCompleted) {
      dispatch(setSurveyIsCompleted(true));
      navigate(ROUTES.DOWNLOAD_APP);
    }
  }, [navigate, dispatch, rewards]);

  return (
    <RewardsForm
      points={totalPoints}
      title={title}
      rewards={rewards.filter((item) => item.status === RewardStatus.IN_PROGRESS)}
      onCollet={handleContinue}
    />
  );
};

export default RewardsStep;
